<template>
  <CopyTrackWrapper
    type="saved_super_page"
    :title="initialSavedAnchorPage.title"
    :uuid="initialSavedAnchorPage.uuid"
    :class="['content-page', {'anchor-page': hasAccordionObjectives}]">
    <LibraryRedesignBanner :title="student.name || ''">
      <template v-slot:breadcrumbList>
        <li v-if="student.uuid"><a :href="libraryFolder(student.uuid)"><i class="fa fa-folder header-folder" /> {{ student.name }}</a></li>
        <li v-if="initialSavedAnchorPage.title" class="active">Goal: {{ initialSavedAnchorPage.title }}</li>
      </template>
    </LibraryRedesignBanner>

    <NoAccessError v-if="hasAccess === false"/>
    <div v-else class="container">
      <div class="row">
        <!-- Main Content -->
        <div v-if="loadError">There was an error loading the page. Please try again.</div>
        <div v-else-if="hasAccess" class="col-sm-8">
          <h1>{{ initialSavedAnchorPage.title }}</h1>
          <AnchorPageLabel v-if="!hasAccordionObjectives" size="md" />&nbsp; <AccessPointLabel
            :level="pageContent.access_point_level"
            :limit-width="false"
            v-if="pageContent.access_point_level"/>&nbsp;
          <span class="label label-success"><i class="fa fa-user"/> Saved to <span class="fs-block">{{ student.name }}</span></span>

          <h2 v-if="!hasAccordionObjectives">Annual Target</h2>
          <BigGoal
            :footer="showAnnualGoalFooter ? true : false"
            :footer2="hasAccordionObjectives"
            :footer3="isPremium && aiFeatureVisibility"
            :event-context="{ obj_level: 'Annual' }"
          >
            <template slot="body">
              <ReviewResults
                v-if="autoReviewLoading || autoReviewError || autoReviewResult"
                title="Goal Review"
                :audit-results="autoReviewResult || []"
                :is-loading="autoReviewLoading"
                :error-message="autoReviewError || ''"
                :criteria-items="filteredGoalsCriteria"
                @close="closeReviewResults"
              />
              <CopyTrackWrapper
                v-if="editableText.annualText"
                type="annual_target"
                level="Annual"
                :title="initialSavedAnchorPage.title"
                :uuid="initialSavedAnchorPage.uuid"
                :class="{ 'anchor-page-wiki-text': hasAccordionObjectives }"
              >
                <EditableWikiTextArea
                  v-model="editableText.annualText"
                  :strategies="strategyMap"
                  @textEdited="annualTextEdited"
                  @textReset="processTextReset"
                  :object-id="pageContent.uuid"
                >
                  <template slot="translateButton">
                    <span v-if="aiFeatureVisibility" class="separator"><TranslateButton @translationLanguageChanged="(lang) => {processLanguageChange(lang)}" /></span>
                  </template>
                </EditableWikiTextArea>
                <TranslationContainer
                  :goal_title="pageContent.title"
                  :goal_text="editableText.annualText"
                  :goal_uuid="pageContent.uuid"
                  :edited="true"
                  :external-open.sync="openTranslationResults"
                  :requested-language="translationLanguage"
                />
              </CopyTrackWrapper>
            </template>
            <template
              v-slot:footer
              v-if="hasPMAs">
              <ResourceListContainer
                :uuids="pageContent.goal_resources"
                :is-indented="hasAccordionObjectives"
                :show-resource-tile="hasAccordionObjectives"
                :object-id="pageContent.uuid"
              />
            </template>

            <template
              v-if="hasAccordionObjectives"
              v-slot:footer2>
              <GoalExampleContent
                @toggle="toggledQuarterlyObjectives"
                :initially-expand="true"
                :is-indented="hasAccordionObjectives"
                key="accordion-objectives">
                <template v-slot:label>
                  <img
                    src="~@/assets/quarterly-objectives-icon.svg"
                    class="objectives-icon--button"
                    aria-hidden="true">Objectives
                </template>
                <ReviewResults
                  v-if="hasObjectivesCriteria && (autoReviewLoading || autoReviewError || (objectivesReviewResult && objectivesReviewResult.audit_results))"
                  title="Objectives Review"
                  :audit-results="objectivesReviewResult ? objectivesReviewResult.audit_results : []"
                  :is-loading="autoReviewLoading"
                  :error-message="autoReviewError || ''"
                  :criteria-items="filteredObjectivesCriteria"
                  @close="closeObjectivesReviewResults"
                />
                <AccordionObjectivesList
                  :access-info="accessInfo"
                  :print-permissions="actionPermissions.printAssessment"
                  :page-uuid="pageContent.uuid"
                  :objectives="pageContent.objectives"
                  :levelled-objective-texts="editableText.objectiveTexts"
                  @objective:edited="objectiveTextEdited"
                  :editable="!accessInfo.blur"
                />
              </GoalExampleContent>
            </template>

            <template
              v-slot:footer2
              v-else-if="pageContent.goal_example_text">
              <GoalExampleContent @toggle="clickedExampleContent">
                <CopyTrackWrapper
                  type="goal-example"
                  level="Annual"
                  :title="initialSavedAnchorPage.title"
                  :uuid="initialSavedAnchorPage.uuid">
                  <WikiText
                    :text="pageContent.goal_example_text"
                  />
                </CopyTrackWrapper>
              </GoalExampleContent>
            </template>

            <template
              v-slot:footer3
              v-if="isPremium && aiFeatureVisibility">
              <GoalExampleContent
                @toggle="toggledSDIStrategies"
                :initially-expand="true"
                :is-indented="hasAccordionObjectives"
                key="sdi-strategies">
                <template v-slot:label>
                  <i
                    class="fa fa-solid fa-wrench sdis-icon--button"
                    aria-hidden="true"/>Specially Designed Instructional Strategies
                </template>
                <SDIStrategies
                  :goal-text="pageContent.plain_goal_text"
                  :goal-grades="pageContent.grades"
                  :class="{'acc-objectives-indent': hasAccordionObjectives}"
                />
              </GoalExampleContent>
            </template>
          </BigGoal>

          <template v-if="hasEditableObjectives && !hasAccordionObjectives">
            <h2>Quarterly Objectives</h2>
            <AnchorPageObjectives
              :items="pageContent.assessment_items"
              :access-info="accessInfo"
              :print-permissions="actionPermissions.printAssessment"
              :page-uuid="pageContent.uuid"
              :objectives="initialSavedAnchorPage.objectives"
              :levelled-objective-texts="editableText.objectiveTexts"
              :editable="true"
              @objective:edited="objectiveTextEdited"
              :objective-resource-uuids="pageContent.objective_resources"/>
          </template>
        </div>

        <!-- Side Bar -->
        <div class="col-sm-4">

          <!-- Library Actions Panel -->
          <LibraryContentPanel
            :content-type="CONTENT_TYPES.SAVED_SUPER_PAGE"
            :content-save-status="contentSaveStatus"
            :content-last-save="initialSavedAnchorPage.updated_at | formatTimeAgo"
            :source-content-uuid="initialSavedAnchorPage.goal_uuid"
            :copy-content-props="{
              personalized: unsavedChanges,
              gradeLevelGoal: editableText.annualText,
              adaptedGoals: objectiveDataForPrinting,
              goalUuid: initialSavedAnchorPage.goal_uuid,
              originalStudent: student,
              anchorPage: true
            }"
            @save="saveChanges"
            @discard="discardChanges"
            @print="togglePrintView(true)"
          />
          <!-- Share Link Panel -->
          <ShareGoalPanel
            :uuid="uuid"
            :source-uuid="initialSavedAnchorPage.goal_uuid"
            v-if="initialSavedAnchorPage.goal_uuid"
            :anchor-page="true"/>

          <ProgressMonitoringPanel
            v-if="initialSavedAnchorPage.title && initialSavedAnchorPage.uuid && !hasPMAs"
            :content-type="CONTENT_TYPES.SAVED_SUPER_PAGE"
            :content-uuid="initialSavedAnchorPage.uuid"
            :content-title="initialSavedAnchorPage.title" />

          <AutoReviewButton
            v-if="showGoalReview"
            :content-uuid="initialSavedAnchorPage.uuid"
            :criteria-set-name="contentReviewCriteriaSetName"
            @click="openAutoReviewModal"
          />

          <AutoReviewModal
            :is-open="isAutoReviewModalOpen"
            :criteria-set-name="contentReviewCriteriaSetName"
            :criteria-by-type="contentReviewCriteria"
            :goal-grades="pageContent.grades"
            :goal-subjects="pageContent.subjects"
            @close="closeAutoReviewModal"
            @review="handleAutoReview"
          />

          <!-- Standards/Staircase -->
          <template v-if="fetchingStandards || fetchingStaircase">
            <p class="text-muted"><i class="fa fa-spin fa-circle-o-notch" /> Loading...</p>
          </template>

          <template v-if="keyStandard">
            <h2>{{ keyStandard.standard_set }}</h2>
            <CopyTrackWrapper
              type="standard"
              :title="initialSavedAnchorPage.title"
              :uuid="initialSavedAnchorPage.uuid">
              <PowerStandard
                v-if="!hasAccordionObjectives"
                :standard="keyStandard"/>
              <StandardsAccordion
                v-else
                :standards="keyStandardArray"
                :should-expand-first-standard="true"/>
            </CopyTrackWrapper>
          </template>

          <GoalContentPageStandards
            :show-edit-link="editorVisibility"
            :content-profile-id="profileId"
            :grouped-standards="groupedSidebarStandards"
            :should-expand-first-standard="!hasAccordionObjectives"
          />

          <template v-if="staircaseContent && staircaseContent.length > 0">
            <h2 :class="{ 'sidebar-section': hasAccordionObjectives }">Standard Staircase</h2>
            <CopyTrackWrapper
              type="staircase"
              :title="pageContent.title"
              :uuid="pageContent.uuid"
              :demo="pageContent.demo">
              <Staircase
                :page-uuid="pageContent.uuid"
                :grades="pageContent.grades"
                :goal-staircase="staircaseContent"
                :access-point-level="pageContent.access_point_level"
                :access-point-grade-level-uuid="pageContent.access_grade_level_uuid"
                :show-anchor-page-label="!hasAccordionObjectives"
              />
            </CopyTrackWrapper>
          </template>

        </div>
      </div>

      <SubmitFeedback
        :title="initialSavedAnchorPage.title"
        :uuid="initialSavedAnchorPage.uuid"
        type="Saved Anchor Page"
        :subject="pageContent.subject" />

      <PrintableSavedAnchorPage
        @update:show="togglePrintView($event)"
        @print="onPrint"
        v-if="showPrintablePage"
        :key-standard="keyStandard"
        :title="initialSavedAnchorPage.title"
        :annual-text="editableText.annualText"
        :goal-example-text="pageContent.goal_example_text"
        :objectives="objectiveDataForPrinting"
        :student-name="student.name"/>

      <LimitActionModal :show.sync="showBlocker" :action-permissions="actionPermissions.printSavedAnchor" />
    </div>

    <div class="print-message">This page is intentionally left blank.</div>
  </CopyTrackWrapper>
</template>

<script>
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex'
import {
  anchorPageMixin,
  trackAnchorContentEditedMixin,
  autoReviewMixin
} from '@/components/goals/common/goal_content_mixin'
import { authorizationCheckMixin } from '@/router/authorization_mixin'
import EditableWikiTextArea from '@/components/EditableWikiTextArea'
import ProgressMonitoringPanel from '@/components/goals/ProgressMonitoringPanel'
import PrintableSavedAnchorPage from '@/components/goals/PrintableSavedAnchorPage'
import LimitActionModal from '@/components/LimitActionModal'
import ShareGoalPanel from '@/components/goals/ShareGoalPanel'
import LibrarySaveChangesButtons from '@/components/goals/LibrarySaveChangesButtons'
import AnchorPageObjectives from '@/components/goals/AnchorPageObjectives'
import CopyGoalForAnotherStudentButton from '@/components/goals/CopyGoalForAnotherStudentButton'
import LibraryBanner from '@/components/library/LibraryBanner'
import LibraryRedesignBanner from '@/components/library/LibraryRedesignBanner'
import AccessPointLabel from '@/components/goals/AccessPointLabel'
import StandardsAccordion from '@/components/goals/StandardsAccordion'
import { setTitle } from '@/util/title'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { shouldShowGoalReview } from '@/util/goals'
import { getLibraryGoal, saveGoalForStudent } from '@/api/library'
import { pmaSvg } from '@/icons'
import SDIStrategies from '@/components/sdistrategies/SDIStrategies.vue'
import { toolkitUrls, goalbookUrls } from '@/links'
import TranslateButton from '@/components/goals/TranslateButton.vue'
import TranslationContainer from '@/components/goals/TranslationContainer.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import { CONTENT_SAVE_STATES } from '@/util/contentSaveStates'
import NoAccessError from '@/components/NoAccessError.vue'
import AutoReviewButton from '@/components/goals/AutoReviewButton'
import AutoReviewModal from '@/components/goals/AutoReviewModal'
import ReviewResults from '@/components/goals/ReviewResults'
export default {
  name: 'SavedAnchorPage',
  mixins: [authorizationCheckMixin, anchorPageMixin, trackAnchorContentEditedMixin, autoReviewMixin],
  components: {
    NoAccessError,
    LibraryContentPanel,
    SDIStrategies,
    EditableWikiTextArea,
    ShareGoalPanel,
    ProgressMonitoringPanel,
    PrintableSavedAnchorPage,
    LimitActionModal,
    LibrarySaveChangesButtons,
    AnchorPageObjectives,
    CopyGoalForAnotherStudentButton,
    LibraryBanner,
    LibraryRedesignBanner,
    AccessPointLabel,
    StandardsAccordion,
    TranslateButton,
    TranslationContainer,
    AutoReviewButton,
    AutoReviewModal,
    ReviewResults
  },
  props: {
    uuid: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      // the saved anchor page document from the API
      // will serve as the initial content for the editable areas (i.e. we should not be mutating this object at all)
      initialSavedAnchorPage: {},

      // used to serve as the editable content v-models, which will be kept in sync with corresponding content in customanchor store
      // kind of extraneous, but this is mainly for the sake of keeping the customanchor store "immutable" and only being updated through actions/mutations, rather than binding directly using v-model
      editableText: {
        annualText: '',
        objectiveTexts: []
      },

      student: {
        name: null,
        uuid: null
      },
      saving: false,
      justSaved: false,
      saveError: null,
      showPrintablePage: false,
      showBlocker: false,
      CONTENT_TYPES,
      pmaSvg: pmaSvg,
      openTranslationResults: false,
      translationLanguage: '',
      ...toolkitUrls,
      ...goalbookUrls,
      loadError: false,
      hasAccess: undefined
    }
  },
  computed: {
    CONTENT_SAVE_STATES() {
      return CONTENT_SAVE_STATES
    },
    ...mapGetters('visibility', ['editorVisibility', 'goalReviewVisibility', 'aiFeatureVisibility', 'betaVisibility']),
    ...mapGetters('user', ['authInfo', 'isPremium', 'userId']),
    ...mapGetters('library', ['getUserRoleForFolder']),
    // customanchor store to manage the state of editable content
    ...mapGetters('customanchor', ['savedAnchorPayload']),
    ...mapGetters('goaldetail', ['customizedGoal']),
    ...mapState('goaldetail', ['fetching']),
    contentSaveStatus() {
      if (this.saving) {
        return CONTENT_SAVE_STATES.SAVING
      }
      if (this.justSaved) {
        return CONTENT_SAVE_STATES.JUST_SAVED
      }
      if (this.unsavedChanges) {
        return CONTENT_SAVE_STATES.UNSAVED
      }
      return CONTENT_SAVE_STATES.SAVED
    },
    hasEditableObjectives() {
      return (
        this.initialSavedAnchorPage.objectives && this.initialSavedAnchorPage.objectives.length > 0
      )
    },
    saveButtonText() {
      if (this.saving) {
        return 'Saving...'
      }
      if (this.unsavedChanges) {
        return 'Save Changes'
      }
      if (this.justSaved) {
        return 'Saved'
      }
      return 'No Changes Made'
    },
    unsavedChanges() {
      return (
        this.editableText.annualText !== this.initialSavedAnchorPage.grade_level_goal ||
        this.editableText.objectiveTexts.some((text, index) => {
          return (
            this.hasEditableObjectives &&
            text !== this.initialSavedAnchorPage.objectives[index].text
          )
        })
      )
    },
    objectiveDataForPrinting() {
      return (
        (this.hasEditableObjectives &&
          this.initialSavedAnchorPage.objectives.map((obj, ind) => {
            return {
              ...obj,
              text: this.editableText.objectiveTexts[ind]
            }
          })) ||
        []
      )
    },
    showGoalReview() {
      return !this.fetching && shouldShowGoalReview({
        goalCustomized: this.customizedGoal,
        contentReviewCriteriaSetName: this.contentReviewCriteriaSetName,
        aiFeatureVisibility: this.aiFeatureVisibility,
        goalReviewVisibility: this.goalReviewVisibility,
        goalUuid: this.initialSavedAnchorPage.uuid,
        contentReviewCriteria: this.contentReviewCriteria
      })
    }
  },
  methods: {
    ...mapActions('customanchor', ['initializeCustomAnchorPage', 'fetchGoalTranslationLanguages']),
    ...mapActions('contentProfile', ['fetchContentReviewCustomization']),
    ...mapActions('contentAccess', ['trackPrint']),
    ...mapActions('goaldetail', ['fetchAllResources']),
    ...mapActions('library', ['fetchCurrentContentFolder']),
    ...mapMutations('library', ['clearCurrentContentFolder']),
    initializeEditableTextFromSaved() {
      // initialize editableText data
      this.editableText.annualText = this.initialSavedAnchorPage.grade_level_goal
      this.editableText.objectiveTexts = this.initialSavedAnchorPage.objectives
        ? this.initialSavedAnchorPage.objectives.map(obj => obj.text)
        : []

      // initialize the "synced" fields in customanchor state
      this.initializeCustomAnchorPage({
        contentUuid: this.initialSavedAnchorPage.goal_uuid,
        primaryGoalText: this.initialSavedAnchorPage.grade_level_goal,
        objectivesArray: this.initialSavedAnchorPage.objectives || []
      })
      this.fetchGoalTranslationLanguages()
    },
    processTextReset(goalText) {
      this.annualTextEdited(goalText)
      this.openTranslationResults = false
    },

    async loadPageContent() {
      this.loadError = false
      this.resetAutoReviewState()
      try {
        let resp = await getLibraryGoal(this.uuid, {})
        this.hasAccess = true
        this.initialSavedAnchorPage = resp
        this.student.name = resp.student_name
        this.student.uuid = resp.student_uuid

        await this.fetchPageContent({
          type: CONTENT_TYPES.SUPER_PAGE,
          attribute: 'uuid',
          value: this.initialSavedAnchorPage.goal_uuid,
          params: {
            with_access_points: 1,
            content_profile: this.profileId,
            beta_mode: this.betaVisibility
          }
        })
        if ((!this.initialSavedAnchorPage.objectives || this.initialSavedAnchorPage.objectives.length < 1) && this.pageContent.objectives?.length > 0) {
          // get objectives from pageContent since they weren't saved initially
          this.initialSavedAnchorPage.objectives = this.pageContent.objectives
        }

        this.initializeEditableTextFromSaved()

        setTitle(`${this.initialSavedAnchorPage.title} | Saved Anchor Page`)

        this.$store.commit('customanchor/setShareStatus', {
          shared: resp.shared,
          shareLink: resp.share_link
        })

        this.setAccessPointsForContent(this.pageContent)

        await this.fetchAllResources()

        const userFolderRole = this.getUserRoleForFolder(resp.student_uuid)
        this.$sendEvent('Viewed Library Anchor Page', {
          user_folder_role: userFolderRole
        })
      } catch (err) {
        if (err.status_code === 403) {
          this.hasAccess = false
          setTitle('Saved Anchor Page')
        } else {
          console.error('loadPageContent', err)
          this.loadError = true
        }
      }
    },
    saveChanges() {
      this.saving = true
      this.justSaved = false
      saveGoalForStudent(
        this.initialSavedAnchorPage.uuid,
        this.savedAnchorPayload,
      )
        .then(resp => {
          this.saving = false
          this.setJustSaved()
          this.initialSavedAnchorPage = resp
          this.initializeEditableTextFromSaved()
        })
        .catch(err => {
          console.error('Error saving saved anchor page', err)
          this.saveError = err
          this.saving = false
        })

      this.$sendEvent('Clicked Save Changes', {
        uuid: this.uuid,
        type: CONTENT_TYPES.SAVED_SUPER_PAGE
      })
    },
    discardChanges() {
      if (confirm('Are you sure you want to discard any unsaved changes?')) {
        this.initializeEditableTextFromSaved()
      }
    },
    setJustSaved() {
      this.justSaved = true
      setTimeout(() => {
        this.justSaved = false
      }, 3000)
    },
    onPrint() {
      this.trackPrint({
        contentType: CONTENT_TYPES.SAVED_SUPER_PAGE,
        contentTitle: this.initialSavedAnchorPage.title,
        otherData: {
          superpage_uuid: this.initialSavedAnchorPage.goal_uuid
        }
      })
    },
    togglePrintView(flag) {
      if (flag) {
        this.$sendEvent('Clicked Print Saved Anchor Page', {
          access: !this.actionPermissions.printSavedAnchor.block,
          uuid: this.uuid
        })
      }
      if (flag && this.actionPermissions.printSavedAnchor.block) {
        this.showBlocker = true
        return
      }
      this.showPrintablePage = flag
    },
    processLanguageChange(lang) {
      this.translationLanguage = lang
      this.openTranslationResults = true
    },
    getAutoReviewGoalText() {
      return this.editableText?.annualText || this.pageContent?.goal_text || ''
    },
    getAutoReviewObjectiveTexts() {
      if (this.editableText?.objectiveTexts?.length) {
        return [...this.editableText.objectiveTexts]
      }
      return this.pageContent?.objectives ? this.pageContent.objectives.map(obj => obj.text) : []
    },
    getAutoReviewGoalUuid() {
      return this.initialSavedAnchorPage?.goal_uuid || this.pageContent?.uuid
    }
  },
  eventContext() {
    return {
      goal_subject: this.pageContent.subject,
      goal_subject_domains: this.pageContent.subject_domains,
      goal_grades: this.pageContent.grades,
      goal_uuid: this.pageContent.uuid,
      goal_title: this.pageContent.title,
      goal_standards:
        this.pageContent.standard_set_refs &&
        this.pageContent.standard_set_refs.map(setRef => setRef.standard_long_ref),
      goal_author_id: this.pageContent.author_id,
      goal_is_anchor: this.pageContent.super_page,
      goal_has_objectives: this.hasObjectives,
      goal_is_library: true,
      is_library: true,
      saved_goal_uuid: this.uuid,
      student_uuid: this.student.uuid,

      goal_has_access_points: this.areAccessPointsPublished,
      goal_access_point_level: this.pageContent.access_point_level,
      goal_access_grade_level_uuid: this.pageContent.access_grade_level_uuid
    }
  },
  watch: {
    $route: function() {
      this.loadPageContent()
    },
    profileId: function () {
      this.$store.commit('goaldetail/setCustomizedGoal', null)
      this.loadPageContent()
      this.fetchContentReviewCustomization()
    }
  },
  created() {
    this.loadPageContent()
    this.fetchCurrentContentFolder({
      userUuid: this.userId,
      contentUuid: this.uuid,
      contentType: CONTENT_TYPES.SAVED_SUPER_PAGE
    })
    this.fetchContentReviewCustomization()
    this.autoReviewIsSavedGoal = true
  },
  beforeDestroy() {
    this.clearCurrentContentFolder()
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';
@import '~@/mixins.less';
h1 small {
  font-size: 18px;
}
.label {
  padding: 0.3em 0.7em 0.4em;
  font-size: 85%;
}

/deep/ .panel-body {
  padding-bottom: 0;
}
.separator {
  border-left: 1px solid #ADADAD;
  padding-left: 8px;
  line-height: 24px;
  font-size: 18px;
  float: left;
  margin-left: 8px;
}
</style>
